import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { MenuItem } from 'app/models/MenuItem';
import { User } from 'app/models/User';
import {
	ADMIN_MENU_ITEMS,
	AVAILABLE_FORM_ITEMS,
	CHART_MENU_ITEMS,
	DEFAULT_MENU_ITEMS,
} from 'app/shared/data/available-forms-data';
import { RouteInfo } from 'app/sidebar/sidebar.component';
import { AvailableForm } from 'app/models/AvailableForm';
import { map, Observable, of } from 'rxjs';
import { Configuration } from 'app/models/Configuration';
import { ClientsService } from './clients.service';

@Injectable({
	providedIn: 'root',
})
export class AvailableFormsService {
	private user: User | null;
	private defaultMenuItems: MenuItem[] = [];
	private userSpecificForms: MenuItem[] = [];

	constructor(private userService: UserService, private clientService: ClientsService) {
		this.userService.currentUser.subscribe((user) => {
			this.user = user?.user;
		});
	}

	getUserMenuItems(): Observable<MenuItem[]> {
		if (!this.user) return of([]);

		if (this.userSpecificForms && this.userSpecificForms?.length > 0) {
			return of(this.userSpecificForms);
		}

		this.defaultMenuItems = this.buildDefaultMenuItems();

		const formsObservable = this.userService.isReadOnlyView()
			? this.getClientEnabledMenuItemsOnly(this.user.clientId)
			: of(this.getUserSpecificForms());

		return formsObservable.pipe(
			map((forms) => {
				const sortedForms = this.sortMenuItems(forms);
				this.userSpecificForms = this.defaultMenuItems.concat(sortedForms);
				return this.userSpecificForms;
			})
		);
	}

	/**
	 * Transforms user forms data to legacy RouteInfo[] format to maintain backward compatibility with components pending migration to getUserMenuItems().
	 * @deprecated Use getUserMenuItems() instead. This method will be removed in future.
	 * @returns {Observable<RouteInfo[]>} Observable array of route info items
	 */
	getUserLegacyRouteInfo(): Observable<RouteInfo[]> {
		console.log('Using optimized route info approach...');

		if (this.userSpecificForms?.length > 0) {
			return of(this.mapMenuItemsToRouteInfos(this.userSpecificForms));
		}

		return this.getUserMenuItems().pipe(map((menuItems) => this.mapMenuItemsToRouteInfos(menuItems)));
	}

	/**
	 * Transforms user forms data to legacy AvailableForm[] format to maintain backward compatibility with components pending migration to getUserMenuItems().
	 * @deprecated Use getUserMenuItems() instead. This method will be removed in future.
	 * @returns {Observable<AvailableForm[]>} Observable array of available forms
	 */
	getUserLegacyAvailableForms(): Observable<AvailableForm[]> {
		console.log('Using optimized available forms approach...');

		if (this.userSpecificForms?.length > 0) {
			return of(this.mapMenuItemsToAvailableForms(this.userSpecificForms));
		}

		return this.getUserMenuItems().pipe(map((menuItems) => this.mapMenuItemsToAvailableForms(menuItems)));
	}

	/**
	 * Retrieves list of menu items that are enabled for the client.
	 * @returns {MenuItem[]}
	 */
	getClientEnabledMenuItemsOnly(clientId: string): Observable<MenuItem[]> {
		if (!clientId) {
			console.error('No clientId found for user');
			return of([]);
		}

		return this.clientService.getClientEnabledFormsConfig(clientId).pipe(
			map((config: Configuration | null) => {
				if (!config?.value) {
					return AVAILABLE_FORM_ITEMS;
				}
				const enabledFormIds = config.value.split(',').map((id) => id.trim());
				return this.filterMenuItemsByIds(AVAILABLE_FORM_ITEMS, enabledFormIds);
			})
		);
	}

	/**
	 * Retrieves full list of available items and marks them as enabled based on client's configuration.
	 * @deprecated This method will be removed in future.
	 * @returns {AvailableForm[]} An array of `AvailableForm` objects, each with an `isEnabled` property indicating its status for the client.
	 */
	getClientEnabledLegacyForms(clientId: string): Observable<AvailableForm[]> {
		if (!clientId) {
			console.error('No clientId found for user');
			return of([]);
		}

		return this.clientService.getClientEnabledFormsConfig(clientId).pipe(
			map((config: Configuration | null) => {
				if (!config?.value) {
					return this.getAllAvailableLegacyForms().map((form) => ({ ...form, isEnabled: false }));
				}
				const enabledFormIds = config.value.split(',').map((id) => id.trim());
				return this.getAllAvailableLegacyForms().map((form) => ({
					...form,
					isEnabled: enabledFormIds.includes(form.id),
				}));
			})
		);
	}

	/**
	 * Retrieves a list of available forms with `isEnabled` set based on client configuration, combined with charts for component-specific enabling.
	 * @deprecated This method will be removed in the future.
	 * @returns {Observable<AvailableForm[]>} An Observable of `AvailableForm` objects, including forms with `isEnabled` set by client configuration and charts with `isEnabled` set to `false` (to be overridden by the component).
	 */
	getClientEnabledLegacyFormsWithCharts(clientId: string): Observable<AvailableForm[]> {
		console.log('Using optimized getClientEnabledLegacyFormsWithCharts approach...');
		const legacyCharts = this.mapMenuItemsToAvailableForms(CHART_MENU_ITEMS);
		return this.getClientEnabledLegacyForms(clientId).pipe(map((forms) => forms.concat(legacyCharts)));
	}

	/**
	 * Retrieves a list of only client-enabled forms combined with all charts for component-specific enabling.
	 * @deprecated This method will be removed in the future.
	 * @returns {Observable<AvailableForm[]>} An Observable of `AvailableForm` objects, including client-enabled forms and all charts with `isEnabled` set to `false` (to be overridden by the component).
	 */
	getClientEnabledLegacyFormsOnlyWithCharts(clientId: string): Observable<AvailableForm[]> {
		console.log('Using optimized getClientEnabledLegacyFormsAndChartsOnly approach...');
		const legacyCharts = this.mapMenuItemsToAvailableForms(CHART_MENU_ITEMS);
		return this.getClientEnabledLegacyFormsOnly(clientId).pipe(
			map((enabledForms) => enabledForms.concat(legacyCharts))
		);
	}

	/**
	 * Retrieves list of only the available items that are enabled for the client.
	 * @deprecated This method will be removed in future.
	 * @returns {AvailableForm[]} An array of `AvailableForm` objects that are marked as enabled.
	 */
	getClientEnabledLegacyFormsOnly(clientId: string): Observable<AvailableForm[]> {
		return this.getClientEnabledLegacyForms(clientId).pipe(map((forms) => forms.filter((form) => form.isEnabled)));
	}

	/**
	 * Retrieves a list of menu items filtered by section and/or type.
	 * @param filters - An object with optional filters for section and type.
	 * @returns {MenuItem[]} An array of MenuItem objects matching the filters.
	 */
	getFilteredMenuItems(filters: { section?: string; type?: string }): MenuItem[] {
		const applyFilters = (menuItems: MenuItem[]): MenuItem[] => {
			return menuItems
				.map((item) => {
					const matches =
						(!filters.section || item.section === filters.section) && (!filters.type || item.type === filters.type);

					const filteredChildren = item.children ? applyFilters(item.children) : undefined;

					if (matches || (filteredChildren && filteredChildren.length > 0)) {
						return {
							...item,
							children: filteredChildren,
						};
					}
					return null;
				})
				.filter((item) => item !== null);
		};

		return applyFilters(AVAILABLE_FORM_ITEMS);
	}

	/**
	 * Retrieves a list of menu items filtered by section and/or type.
	 * @deprecated Use getFilteredMenuItems instead. This method will be removed in the future.
	 * @param filters - An object with optional filters for section and type.
	 * @returns {MenuItem[]} An array of AvailableForm objects matching the filters.
	 */
	getFilteredLegacyForms(filters: { section?: string; type?: string }): AvailableForm[] {
		const filteredForms = this.getFilteredMenuItems(filters);
		return this.mapMenuItemsToAvailableForms(filteredForms);
	}

	getAllMenuItems(): MenuItem[] {
		return [ADMIN_MENU_ITEMS, ...DEFAULT_MENU_ITEMS, ...AVAILABLE_FORM_ITEMS];
	}

	private getAllAvailableLegacyForms(): AvailableForm[] {
		const allForms = this.mapMenuItemsToAvailableForms(AVAILABLE_FORM_ITEMS);
		return allForms;
	}

	// This method sets isEnabled to 'true' for all leafItems. Ensure you're passing filtered list.
	private mapMenuItemsToAvailableForms(menuItems: MenuItem[]): AvailableForm[] {
		return menuItems.flatMap((parentItem) => {
			if (!parentItem.children) return [];

			return parentItem.children.flatMap((categoryItem) => {
				const forms: AvailableForm[] = [];

				// Handle categoryItem as a leaf item if it has a route or if it's a chart
				if (categoryItem.route || categoryItem.type === 'chart') {
					forms.push({
						clientIds: null,
						showInDashboard: categoryItem.type === 'form',
						id: categoryItem.id,
						name: categoryItem.label,
						section: categoryItem.section,
						sectionId: parentItem.id,
						isEnabled: categoryItem.type !== 'chart',
						type: categoryItem.type || 'form',
						formType: categoryItem.formType || null,
						path: categoryItem.route,
					});
				}

				// Handle nested children if they exist
				if (categoryItem.children) {
					forms.push(
						...categoryItem.children
							.filter((leafItem) => leafItem.route)
							.map((leafItem) => ({
								clientIds: null,
								showInDashboard: leafItem.type === 'form',
								id: leafItem.id,
								name: leafItem.label,
								section: leafItem.section,
								sectionId: parentItem.id,
								isEnabled: true,
								type: leafItem.type || 'form',
								formType: leafItem.formType || null,
								path: leafItem.route,
							}))
					);
				}

				return forms;
			});
		});
	}

	// Get order based on section type
	private getOrderForSectionType(type: string | undefined): number | undefined {
		if (!type) return undefined;

		const orderMap: { [key: string]: number } = {
			form: 1,
			'data-management': 2,
			report: 3,
		};

		return orderMap[type];
	}

	// Main mapping function
	private mapMenuItemToRouteInfo(menuItem: MenuItem): RouteInfo {
		const routeInfo: RouteInfo = {
			path: menuItem.route || '', // Only set path if route exists
			title: menuItem.label,
			type: menuItem.children && menuItem.children.length > 0 ? 'sub' : 'link',
			enabled: true,
		};

		// Add collapse property for items with children
		if (menuItem.children && menuItem.children.length > 0) {
			routeInfo.collapse = menuItem.id;
			routeInfo.children = menuItem.children.map((child) => this.mapMenuItemToChildrenItem(child));
		}

		return routeInfo;
	}

	// Map MenuItem to ChildrenItems
	private mapMenuItemToChildrenItem(menuItem: MenuItem): RouteInfo {
		const childItem: RouteInfo = {
			path: menuItem.route || '', // Only set path if route exists
			title: menuItem.label,
			type: menuItem.children && menuItem.children.length > 0 ? 'sub' : menuItem.type,
			enabled: true,
		};

		// Only add order for Forms, Data Management, and Reports sections
		const order = this.getOrderForSectionType(menuItem.type);
		if (order !== undefined) {
			childItem.order = order;
		}

		// Add collapse for items with children
		if (menuItem.children && menuItem.children.length > 0) {
			childItem.collapse = menuItem.type || menuItem.id;

			// Map nested children
			childItem.children = menuItem.children.map(
				(child): RouteInfo => ({
					type: child.type || menuItem.type,
					path: child.route || '',
					title: child.label,
					id: child.id,
					enabled: true,
				})
			);
		}

		return childItem;
	}

	// Function to map an array of MenuItems to RouteInfos
	private mapMenuItemsToRouteInfos(menuItems: MenuItem[]): RouteInfo[] {
		return menuItems.map((menuItem) => this.mapMenuItemToRouteInfo(menuItem));
	}

	private buildDefaultMenuItems(): MenuItem[] {
		const tempDefaultMenuItems = [...DEFAULT_MENU_ITEMS];

		// Add Administrator menu if user is admin
		if (this.userService.isAdmin()) {
			tempDefaultMenuItems.unshift(ADMIN_MENU_ITEMS);
		} else if (
			this.user?.claims?.[this.user.clientId]?.canAddUser ||
			this.user?.claims?.[this.user.clientId]?.canViewUser
		) {
			const clientAdminItem = this.addClientAdminMenuItems();
			if (clientAdminItem) {
				tempDefaultMenuItems.unshift(clientAdminItem);
			}
		}

		// Add Monthly Time Sheet for specific users
		if (this.userService.isAclarianEmployee() || this.userService.isAdmin()) {
			tempDefaultMenuItems.push({
				id: 'monthly-time-sheet',
				label: 'Monthly Time Sheet',
				route: 'forms/monthly-timesheet',
				section: 'Monthly Time Sheet',
			});
		}

		// Add Multi Client Dashboard for users with multiple clients
		if (this.user?.clients?.length > 1) {
			tempDefaultMenuItems.push({
				id: 'multi-client-dashboard',
				label: 'Multi Client Dashboard',
				route: 'multi-client-dashboard',
				section: 'Multi Client Dashboard',
			});
		}

		return tempDefaultMenuItems;
	}

	private addClientAdminMenuItems(): MenuItem | undefined {
		const clientAdminItem: MenuItem = {
			id: 'client-admin',
			label: 'Users',
			children: [],
			section: 'Users',
		};

		if (this.user?.claims?.[this.user.clientId]?.canAddUser) {
			clientAdminItem.children.push({
				id: 'client-users-add',
				label: 'Add User',
				route: 'client-users/add',
				section: 'Client Administrator',
			});
		}

		if (this.user?.claims?.[this.user.clientId]?.canViewUser) {
			clientAdminItem.children.push({
				id: 'client-users-list',
				label: 'User List',
				route: 'client-users/list',
				section: 'Client Administrator',
			});
		}

		return clientAdminItem.children?.length > 0 ? clientAdminItem : undefined;
	}

	private getUserSpecificForms(): MenuItem[] {
		const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
			return items
				.map((item) => {
					// Check if the current item is visible based on claims or admin status
					const isVisible =
						this.userService.isAdmin() ||
						this.user?.claims[this.user?.clientId]?.claims?.some((c) => c.id === item.id && c.visible);

					// Recursively filter children if they exist
					const filteredChildren = item.children ? filterMenuItems(item.children) : undefined;

					// Include the item if it's visible or has visible children
					if (isVisible || (filteredChildren && filteredChildren.length > 0)) {
						return {
							...item,
							children: filteredChildren, // Include filtered children (undefined if no children)
						};
					}
					return null;
				})
				.filter((item) => item !== null); // Remove null entries
		};

		return filterMenuItems(AVAILABLE_FORM_ITEMS);
	}

	// recursively sort menu items with fixed order for forms, data management, and reports
	private sortMenuItems(menuItems: MenuItem[]): MenuItem[] {
		return menuItems
			.map((item) => ({
				...item,
				children: item.children ? this.sortMenuItems(item.children) : undefined,
			}))
			.sort((a, b) => {
				const labelA = a.label || '';
				const labelB = b.label || '';

				// Define the specific order for Forms, Data Management, Reports
				const fixedOrder = ['Forms', 'Data Management', 'Reports'];
				const indexA = fixedOrder.indexOf(labelA);
				const indexB = fixedOrder.indexOf(labelB);

				// If both items are in the fixed order array, sort by their position
				if (indexA !== -1 && indexB !== -1) {
					return indexA - indexB;
				}

				// If neither is in fixed order, sort alphabetically
				return labelA.localeCompare(labelB);
			});
	}

	/**
	 * Recursively searches through menu items and returns only those whose IDs are in enabledIds
	 * Also filters children recursively and removes parent items that have no enabled children
	 */
	private filterMenuItemsByIds(menuItems: MenuItem[], enabledIds: string[]): MenuItem[] {
		return menuItems
			.map((item) => {
				const isItemEnabled = enabledIds.includes(item.id);

				// If item has children, recursively filter them
				let filteredChildren: MenuItem[] | undefined = undefined;
				if (item.children) {
					const filtered = this.filterMenuItemsByIds(item.children, enabledIds);
					filteredChildren = filtered.length > 0 ? filtered : undefined;
				}

				const shouldIncludeItem = isItemEnabled || (filteredChildren && filteredChildren.length > 0);

				if (shouldIncludeItem) {
					return {
						...item,
						children: filteredChildren,
					};
				}

				return null;
			})
			.filter((item) => item !== null) as MenuItem[];
	}

	clearOnLogout(): void {
		this.user = null;
		this.defaultMenuItems = [];
		this.userSpecificForms = [];
	}
}
